export type ClientStatusId = 1 | 2
export type ClientTypeId = 1 | 2 | 3

export type ClientRow = {
  id: string
  name: string
  address: string
  phone: string
  email: string
  statusId: ClientStatusId
  principalContact: string
  typeId: ClientTypeId
  showProjectStatus?: boolean
  showAdvisoryMapping?: boolean
}

export type ClientDetail = ClientRow & {
  powerBIWorkspace?: string
  powerBIWorkspaceName?: string
  canUseXPMUserGroup?: boolean
  platformType?: Array<number | string>
  street?: string
  suburb?: string
  state?: string
  postcode?: string
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
}

export type ClientGridApiRow = {
  clientId?: string
  ClientId?: string
  name?: string
  Name?: string
  address?: string
  Address?: string
  phone?: string
  Phone?: string
  email?: string
  Email?: string
  statusId?: number
  StatusId?: number
  contact?: string
  Contact?: string
  typeId?: number
  TypeId?: number
  showProjectStatus?: boolean
  ShowProjectStatus?: boolean
  showAdvisoryMapping?: boolean
  ShowAdvisoryMapping?: boolean
}

export type ClientGridApiResponse = ClientGridApiRow[] | { clients?: ClientGridApiRow[] }

export type ClientDetailApiResponse = {
  platformType?: Array<number | string>
  PlatformType?: Array<number | string>
  client?: {
    clientId?: string
    ClientId?: string
    name?: string
    Name?: string
    address?: string
    Address?: string
    phone?: string
    Phone?: string
    email?: string
    Email?: string
    statusId?: number
    StatusId?: number
    contact?: string
    Contact?: string
    typeId?: number
    TypeId?: number
    showProjectStatus?: boolean
    ShowProjectStatus?: boolean
    showAdvisoryMapping?: boolean
    ShowAdvisoryMapping?: boolean
    canUseXPMUserGroup?: boolean
    CanUseXPMUserGroup?: boolean
    powerBIWorkspace?: string
    PowerBIWorkspace?: string
    powerBiWorkspace?: string
    PowerBiWorkspace?: string
    powerBIWorkspaceName?: string
    PowerBIWorkspaceName?: string
    powerBiWorkspaceName?: string
    PowerBiWorkspaceName?: string
    street?: string
    Street?: string
    suburb?: string
    Suburb?: string
    state?: string
    State?: string
    postcode?: string
    Postcode?: string
    primaryColor?: string
    PrimaryColor?: string
    secondaryColor?: string
    SecondaryColor?: string
    textColor?: string
    TextColor?: string
  }
}

export type ClientSavePayload = {
  clientId?: string
  statusId: ClientStatusId
  name: string
  phone: string
  email: string
  contact: string
  address: string
  street?: string
  suburb?: string
  state?: string
  postcode?: string
  powerBIWorkspace?: string
  powerBIWorkspaceName?: string
  databaseName?: string
  showProjectStatus?: boolean
  showAdvisoryMapping?: boolean
  logo?: string
  isImage?: boolean
  typeId: ClientTypeId
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
}

export type ClientSaveApiPayload = {
  ClientId?: string
  StatusId: ClientStatusId
  TypeId: ClientTypeId
  Name: string
  Contact: string
  Phone: string
  Email: string
  Address: string
  Street?: string
  Suburb?: string
  State?: string
  Postcode?: string
  PowerBIWorkspace?: string
  PowerBIWorkspaceName?: string
  DatabaseName?: string
  ShowProjectStatus?: boolean
  ShowAdvisoryMapping?: boolean
  Logo?: string
  IsImage?: boolean
  PrimaryColor?: string
  SecondaryColor?: string
  TextColor?: string
}

export type ClientSaveApiResponse = {
  clientId?: string
  ClientId?: string
  name?: string
  Name?: string
}

export type PbiGroupOption = {
  value: string | number
  name: string
}

export type UploadClientLogoPayload = {
  clientId: string
  base64image: string
  contentType?: string
}

export type ClientLogoResponse = {
  key?: string | null
}

export type FeatureAccessUserTypeRow = {
  permission: string
  userTypeId: number | null
  userTypeName: string
  enabled: boolean
}

export type FeatureAccessUserGroupOption = {
  groupId: string
  name: string
}

export type FeatureAccessGroupRow = {
  permission: string
  userGroupId: string | null
  userGroupName: string
  enabled: boolean
}

export type FeatureAccessPermission = {
  permission: string
  userGroupId?: string | null
  rows: FeatureAccessUserTypeRow[]
  groupRows: FeatureAccessGroupRow[]
}

export const featureAccessPermissionLabels: Record<string, string> = {
  viewAgentBuilder: 'PracIntel Agent Builder View Access',
  updateAgentBuilder: 'PracIntel Agent Builder Update Access',
  viewAdvisoryAI: 'AdvicePrep View Access',
  updateAdvisoryAI: 'AdvicePrep Update Access',
  viewAnnualCompliance: 'CompVerify View/Update Access',
  viewCapacityPlanner: 'PracPlanner View/Update Access',
  viewFileUpload: 'File Upload View/Update Access',
  viewNotification: 'PracIntel View Access',
  updateNotification: 'PracIntel Update Access',
  viewSurveyForm: 'Survey Forms View Access',
  editSurveyForm: 'Survey Forms Update Access',
}

export const featureAccessPermissionAliases: Record<string, string> = {
  viewAgentBuilder: 'viewAgentBuilder',
  updateAgentBuilder: 'updateAgentBuilder',
  viewAdvisoryAI: 'viewAdvisoryAI',
  updateAdvisoryAI: 'updateAdvisoryAI',
  viewAnnualCompliance: 'viewAnnualCompliance',
  viewCapacityPlanner: 'viewCapacityPlanner',
  viewFileUpload: 'viewFileUpload',
  viewNotification: 'viewNotification',
  updateNotification: 'updateNotification',
  viewSurveyForm: 'viewSurveyForm',
  addSurveyForm: 'editSurveyForm',
  editSurveyForm: 'editSurveyForm',
}

export const featureAccessUserTypeLabels: Record<string, string> = {
  ClientAdmin: 'ClientAdmin',
  ClientUser: 'ClientUser',
  AdminAdvisor: 'AdvisorAdmin',
  AdvisorAdmin: 'AdvisorAdmin',
}

/**
 * Feature pairs a client cannot hold at once. PracIntel resolves to either Agent Builder
 * or the report builder, never both, so enabling one has to mean the other is off. Mirrors
 * FeatureAccessDefinitions.TryGetExclusiveCounterpart on the API, which rejects a save that
 * breaks the rule.
 */
const exclusiveFeatureKeyPairs: Array<[string, string]> = [['agentBuilder', 'notificationBuilder']]

export const exclusiveFeatureDisplayNames: Record<string, string> = {
  agentBuilder: 'Agent Builder',
  notificationBuilder: 'PracIntel (report builder)',
}

export function getExclusiveCounterpartFeatureKey(featureKey: string): string | null {
  for (const [first, second] of exclusiveFeatureKeyPairs) {
    if (featureKey === first) return second
    if (featureKey === second) return first
  }
  return null
}

export function getFeatureAccessFeatureLabel(featureKey: string, featureName: string) {
  return exclusiveFeatureDisplayNames[featureKey] ?? featureName
}

export const hiddenFeatureAccessUserTypes = new Set(['User', 'Owner', 'UnregisteredUser', 'Admin'])
export const editableFeatureAccessUserTypeIds = new Set([2, 3, 7])

export function normalizeFeatureAccessPermission(permission: string) {
  return featureAccessPermissionAliases[permission] ?? permission
}

export function getFeatureAccessPermissionLabel(featureName: string, permission: string) {
  return featureAccessPermissionLabels[normalizeFeatureAccessPermission(permission)] ?? `${featureName} / ${permission}`
}

export function normalizeFeatureAccessUserTypeName(userTypeName: string) {
  return featureAccessUserTypeLabels[userTypeName] ?? userTypeName
}

export type FeatureAccessFeature = {
  featureKey: string
  featureName: string
  permissions: FeatureAccessPermission[]
}

export type FeatureAccessResponse = {
  clientId: string
  features: FeatureAccessFeature[]
  metadata?: {
    editablePermissions?: string[]
    groups?: Array<{
      groupId: string
      name: string
    }>
  }
}

export type UpdateFeatureAccessRequest = {
  features: Array<{
    featureKey: string
    permissions: Array<{
      permission: string
      rows: Array<{
        userTypeId: number | null
        enabled: boolean
      }>
      groupRows: Array<{
        userGroupId: string | null
        enabled: boolean
      }>
    }>
  }>
}
